import React, { useContext, useEffect, useMemo, useState } from 'react'
import { yupResolver } from '@hookform/resolvers'
import DateFnsUtils from '@date-io/date-fns'
import CardContents from '@ifca-root/react-component/src/components/CardList/CardContents'
import EmptyList from '@ifca-root/react-component/src/components/CardList/EmptyList'
import MainHeader from '@ifca-root/react-component/src/components/Header/MainHeader'
import { VoiceTextField } from '@ifca-root/react-component/src/components/Input/CustomTextField'
import { ContentWrapper } from '@ifca-root/react-component/src/components/Layout/ContentWrapper'
import Loading from '@ifca-root/react-component/src/components/Loading/Loading'
import {
  Grid,
  IconButton,
  List,
  ListItem,
  ListItemText,
  TextField,
} from '@material-ui/core'
import { Autocomplete } from '@material-ui/lab'
import {
  KeyboardDatePicker,
  MuiPickersUtilsProvider,
} from '@material-ui/pickers'
import { ErrorDialog } from 'components/Dialog/ErrorDialog'
import { ExitConfirmationDialog } from 'components/Dialog/ExitConfirmationDialog'
import { AccountFooter } from 'components/Footer/AccountFooter'
import SnackBarContext from 'containers/App/Store/SnackBarContext'
import {
  ApprovalStatus,
  useCreateFaAssetCountMutation,
  useGetAssetsForCountingLazyQuery,
  useGetFaCategoryDropdownListingQuery,
  useLatestOpenPeriodCheckingDateQuery,
  useUpdateFaAssetCountMutation,
} from 'generated/graphql'
import { SystemMsgs } from 'helpers/Messages/SystemMsg'
import { Controller, useForm } from 'react-hook-form'
import NumberFormat from 'react-number-format'
import { useHistory, useLocation, useParams } from 'react-router-dom'
import * as yup from 'yup'
import LocationOnIcon from '@material-ui/icons/LocationOn'
import EditIcon from '@material-ui/icons/Edit'
import CheckCircleIcon from '@material-ui/icons/CheckCircle'
import WarningIcon from '@material-ui/icons/Warning'
import theme from '@ifca-root/react-component/src/assets/theme'

interface AssetCountFormData {
  count_date: Date
  category_id: string
  remark: string
}

interface AssetForCount {
  fa_asset_id: string
  asset_card_no: string
  name: string
  cost_centre_id: string
  cost_centre_name: string
  location_id: string
  location_name: string
  expected_qty: number
  counted_qty: number | null
  count_note: string | null
}

export const AssetsCountForm = (props: any) => {
  const history = useHistory()
  const location = useLocation()
  const editData: any = location?.state
  const { formMode } = props
  const { CompanyID }: any = useParams()
  const user = JSON.parse(localStorage.getItem('loggedInUser'))

  /* -------------------------------------------- */
  /*                     STATE                    */
  /* -------------------------------------------- */
  const [errorDia, setErrorDia] = useState<boolean>(false)
  const [errMsg, setErrMsg] = useState<string>('')
  const [openExitConf, setOpenExitConf] = useState(false)
  const [record, setRecord] = useState(false)
  const [selectedCategory, setSelectedCategory] = useState<any>(
    editData?.Category || null
  )
  const [assetsForCount, setAssetsForCount] = useState<AssetForCount[]>([])
  const [editingAssetIndex, setEditingAssetIndex] = useState<number | null>(
    null
  )
  const { setOpenSnackBar, setSnackBarMsg }: any = useContext(SnackBarContext)

  let mode = formMode || 'add'
  if (formMode === 'resubmit') {
    mode = 'edit'
  }

  /* -------------------------------------------- */
  /*                   USE FORM                   */
  /* -------------------------------------------- */
  const assetCountFormSchema = yup.object().shape({
    count_date: yup.date().required('Count Date is required'),
    category_id: yup.string().required('Category is required'),
    remark: yup.string().nullable(),
  })

  const { setValue, control, handleSubmit, watch, errors, register } = useForm<
    AssetCountFormData
  >({
    defaultValues: {
      count_date: editData?.count_date
        ? new Date(editData.count_date)
        : new Date(),
      category_id: editData?.category_id || '',
      remark: editData?.remark || '',
    },
    mode: 'onSubmit',
    resolver: yupResolver(assetCountFormSchema),
  })

  /* -------------------------------------------- */
  /*                    QUERY                     */
  /* -------------------------------------------- */
  const {
    loading: categoryLoading,
    data: { getFACategoryDropdownListing } = {
      getFACategoryDropdownListing: [],
    },
  } = useGetFaCategoryDropdownListingQuery({
    fetchPolicy: 'network-only',
    onError: ({ message }) => {
      const error = message?.substring(15)
      setErrMsg(error)
      setErrorDia(true)
    },
  })

  const [
    getAssetsForCounting,
    { loading: assetsLoading },
  ] = useGetAssetsForCountingLazyQuery({
    fetchPolicy: 'network-only',
    onError: ({ message }) => {
      const error = message?.substring(15)
      setErrMsg(error)
      setErrorDia(true)
    },
    onCompleted: data => {
      if (data?.getAssetsForCounting) {
        const assets = data.getAssetsForCounting.map(asset => ({
          fa_asset_id: asset.fa_asset_id,
          asset_card_no: asset.asset_card_no || '',
          name: asset.name,
          cost_centre_id: asset.cost_centre_id,
          cost_centre_name: asset.cost_centre_name || '',
          location_id: asset.location_id,
          location_name: asset.location_name || '',
          expected_qty: asset.expected_qty,
          counted_qty: asset.counted_qty,
          count_note: asset.count_note,
        }))
        setAssetsForCount(assets)
      }
    },
  })

  const {
    loading: openPeriodCheckDateLoading,
    data: { latestOpenPeriodCheckingDate } = {
      latestOpenPeriodCheckingDate: {} as any,
    },
  } = useLatestOpenPeriodCheckingDateQuery({
    fetchPolicy: 'network-only',
    variables: { CompanyID },
    onError: ({ message }) => {
      const error = message?.substring(15)
      setErrMsg(error)
      setErrorDia(true)
    },
  })

  // const categoryList = useMemo(() => {
  //   return getFACategoryDropdownListing?.filter(cat => cat.is_last_node) || []
  // }, [getFACategoryDropdownListing])

  useEffect(() => {
    if (
      (formMode === 'edit' || formMode === 'resubmit') &&
      editData?.category_id
    ) {
      const category = getFACategoryDropdownListing.find(
        c => c.fa_category_id === editData.category_id
      )
      if (category) {
        setSelectedCategory(category)
        getAssetsForCounting({
          variables: {
            CompanyID: CompanyID,
            category_id: editData.category_id,
            asset_count_id: editData.asset_count_id,
          },
        })
      }
    }
  }, [formMode, editData, getFACategoryDropdownListing])

  useEffect(() => {
    if (
      editData?.CountDetails &&
      assetsForCount.length > 0 &&
      (formMode === 'edit' || formMode === 'resubmit')
    ) {
      const updatedAssets = assetsForCount.map(asset => {
        const existingDetail = editData.CountDetails.find(
          d =>
            d.fa_asset_id === asset.fa_asset_id &&
            d.cost_centre_id === asset.cost_centre_id &&
            d.location_id === asset.location_id
        )
        if (existingDetail) {
          return {
            ...asset,
            counted_qty: existingDetail.counted_qty,
            count_note: existingDetail.count_note,
          }
        }
        return asset
      })
      setAssetsForCount(updatedAssets)
    }
  }, [editData?.CountDetails, assetsForCount.length])

  /* -------------------------------------------- */
  /*                   MUTATION                   */
  /* -------------------------------------------- */
  const [
    createAssetCount,
    { loading: createLoading },
  ] = useCreateFaAssetCountMutation({
    onError: ({ message }) => {
      const error = message?.substring(15)
      setErrMsg(error)
      setErrorDia(true)
    },
    onCompleted: () => {
      setOpenSnackBar(true)
      setSnackBarMsg(SystemMsgs.createNewRecord())
      history.push(`/fixed-assets/${CompanyID}/assets-count`)
    },
  })

  const [
    updateAssetCount,
    { loading: updateLoading },
  ] = useUpdateFaAssetCountMutation({
    onError: ({ message }) => {
      const error = message?.substring(15)
      setErrMsg(error)
      setErrorDia(true)
    },
    onCompleted: () => {
      setOpenSnackBar(true)
      setSnackBarMsg(SystemMsgs.updateRecord())
      history.push(`/fixed-assets/${CompanyID}/assets-count`)
    },
  })

  /* -------------------------------------------- */
  /*                   HANDLERS                   */
  /* -------------------------------------------- */
  const handleCategoryChange = (value: any) => {
    if (value) {
      setSelectedCategory(value)
      setValue('category_id', value.fa_category_id)
      setAssetsForCount([])

      getAssetsForCounting({
        variables: {
          CompanyID: CompanyID,
          category_id: value.fa_category_id,
          asset_count_id: editData?.asset_count_id || null,
        },
      })
    } else {
      setSelectedCategory(null)
      setValue('category_id', '')
      setAssetsForCount([])
    }
  }

  const handleCountedQtyChange = (index: number, value: number | null) => {
    const updatedAssets = [...assetsForCount]
    updatedAssets[index] = {
      ...updatedAssets[index],
      counted_qty: value,
    }
    setAssetsForCount(updatedAssets)
  }

  const handleCountNoteChange = (index: number, note: string) => {
    const updatedAssets = [...assetsForCount]
    updatedAssets[index] = {
      ...updatedAssets[index],
      count_note: note,
    }
    setAssetsForCount(updatedAssets)
  }

  const onSubmit = (
    data: AssetCountFormData,
    approvalStatus: ApprovalStatus
  ) => {
    const hasUnCountedItems = assetsForCount.some(
      a => a.counted_qty === null || a.counted_qty === undefined
    )

    if (approvalStatus === ApprovalStatus.Submit && hasUnCountedItems) {
      setErrMsg('Please count all assets before submitting')
      setErrorDia(true)
      return
    }

    if (approvalStatus === ApprovalStatus.Submit && isPeriodClosed) {
      setErrMsg(
        'Cannot submit. The accounting period is closed or does not exist. ' +
          'Please select a date within the open financial period ' +
          (latestOpenPeriodCheckingDate?.StartDate &&
          latestOpenPeriodCheckingDate?.EndDate
            ? `(${latestOpenPeriodCheckingDate.StartDate} to ${latestOpenPeriodCheckingDate.EndDate}).`
            : '.')
      )
      setErrorDia(true)
      return
    }

    if (assetsForCount.length === 0) {
      setErrMsg(
        'No assets to count. Please select a category with registered assets.'
      )
      setErrorDia(true)
      return
    }

    const input = {
      asset_count_id: editData?.asset_count_id || undefined,
      company_id: CompanyID,
      category_id: data.category_id,
      count_date: data.count_date,
      remark: data.remark || null,
      ApprovalStatus: approvalStatus,
    }

    const details = assetsForCount.map(asset => ({
      fa_asset_id: asset.fa_asset_id,
      asset_qty: asset.expected_qty,
      counted_qty: asset.counted_qty ?? asset.expected_qty,
      cost_centre_id: asset.cost_centre_id,
      location_id: asset.location_id,
      count_note: asset.count_note || null,
    }))

    if (formMode === 'add') {
      createAssetCount({ variables: { input, details } })
    } else {
      updateAssetCount({ variables: { input, details } })
    }
  }

  /* -------------------------------------------- */
  /*               PERIOD VALIDATION              */
  /* -------------------------------------------- */
  const watchCountDate =
    watch('count_date') || editData?.count_date || new Date()
  const countDateStr = (() => {
    const d = new Date(watchCountDate)
    const year = d.getFullYear()
    const month = (d.getMonth() + 1).toString().padStart(2, '0')
    const day = d
      .getDate()
      .toString()
      .padStart(2, '0')
    return `${year}-${month}-${day}`
  })()

  const isPeriodClosed =
    latestOpenPeriodCheckingDate?.StartDate &&
    latestOpenPeriodCheckingDate?.EndDate
      ? countDateStr < latestOpenPeriodCheckingDate?.StartDate ||
        countDateStr > latestOpenPeriodCheckingDate?.EndDate
      : false

  /* -------------------------------------------- */
  /*                    HELPERS                   */
  /* -------------------------------------------- */
  const countSummary = useMemo(() => {
    const total = assetsForCount.length
    const counted = assetsForCount.filter(
      a => a.counted_qty !== null && a.counted_qty !== undefined
    ).length
    const discrepancies = assetsForCount.filter(
      a => a.counted_qty !== null && a.counted_qty !== a.expected_qty
    ).length

    return { total, counted, discrepancies }
  }, [assetsForCount])

  /* -------------------------------------------- */
  /*                    FOOTER                    */
  /* -------------------------------------------- */
  const draftFooterOption = {
    name: 'Save as Draft',
    onClick: handleSubmit(data => onSubmit(data, ApprovalStatus.Active)),
    color: 'primary',
    props: { type: 'button' },
  }

  const submitFooterOption = {
    name: 'Submit',
    onClick: handleSubmit(data => onSubmit(data, ApprovalStatus.Submit)),
    color: 'primary',
    props: { type: 'button' },
  }

  let footerOptions: any[]
  if (formMode === 'add' || formMode === 'edit' || formMode === 'resubmit') {
    footerOptions = [draftFooterOption, submitFooterOption]
  } else {
    footerOptions = []
  }

  return (
    <>
      {(categoryLoading ||
        assetsLoading ||
        createLoading ||
        updateLoading ||
        openPeriodCheckDateLoading) && <Loading />}

      <MainHeader
        mainBtn="close"
        onClick={() => {
          history.push(`/fixed-assets/${CompanyID}/assets-count`)
        }}
        smTitle={'Fixed Assets'}
        title={user?.companyName}
        routeSegments={[
          { name: 'Fixed Assets' },
          { name: 'Assets Count', current: true },
        ]}
        rightRouteSegments={[
          {
            name:
              formMode === 'add'
                ? 'New'
                : formMode === 'resubmit'
                ? 'Resubmit'
                : 'Edit',
            current: true,
          },
        ]}
      />

      <ContentWrapper float footer>
        <CardContents
          section={{
            header: { title: 'Count Details' },
          }}
        >
          <MuiPickersUtilsProvider utils={DateFnsUtils}>
            <Controller
              render={({ value, onChange }) => (
                <KeyboardDatePicker
                  value={value}
                  onChange={onChange}
                  label="Count Date"
                  format="dd/MM/yyyy"
                  margin="dense"
                  allowKeyboardControl
                  KeyboardButtonProps={{
                    'aria-label': 'change date',
                  }}
                  required
                  minDate={
                    latestOpenPeriodCheckingDate?.StartDate
                      ? new Date(latestOpenPeriodCheckingDate.StartDate)
                      : undefined
                  }
                  maxDate={
                    latestOpenPeriodCheckingDate?.EndDate
                      ? new Date(latestOpenPeriodCheckingDate.EndDate)
                      : undefined
                  }
                  helperText={
                    isPeriodClosed ? 'Financial Period is closed' : ''
                  }
                  error={isPeriodClosed}
                />
              )}
              name="count_date"
              control={control}
              defaultValue={
                editData?.count_date
                  ? new Date(editData.count_date)
                  : new Date()
              }
            />
          </MuiPickersUtilsProvider>

          {!categoryLoading && (
            <Controller
              render={({ value, onChange }) => {
                const defVal = getFACategoryDropdownListing.find(
                  c => c.fa_category_id === editData?.category_id
                )

                return (
                  <Autocomplete
                    options={
                      getFACategoryDropdownListing?.sort((a, b) =>
                        a?.name?.localeCompare(b?.name)
                      ) || []
                    }
                    getOptionLabel={option => option?.name || ''}
                    fullWidth
                    onChange={(event, value: any) =>
                      handleCategoryChange(value)
                    }
                    defaultValue={defVal || null}
                    disabled={
                      mode === 'edit' &&
                      editData?.ApprovalStatus !== ApprovalStatus.Active
                    }
                    renderOption={props => {
                      return (
                        <div>
                          <div>
                            <span className="xsTitle">{props?.name}</span>
                          </div>
                        </div>
                      )
                    }}
                    renderInput={(params: any) => {
                      return (
                        <TextField
                          {...params}
                          label="Asset Category"
                          style={{ width: '100%' }}
                          required
                          margin="normal"
                          helperText={errors?.category_id?.message}
                          error={errors?.category_id ? true : false}
                        />
                      )
                    }}
                  />
                )
              }}
              name="category_id"
              control={control}
              margin="normal"
              defaultValue={editData?.category_id || ''}
            />
          )}

          <VoiceTextField
            mounted={true}
            label="Remark"
            name="remark"
            setValue={setValue}
            record={record}
            setRecord={setRecord}
            control={control}
            controllerProps={{
              autoComplete: 'off',
              defaultValue: editData?.remark || '',
              ref: register,
            }}
          />
        </CardContents>

        <CardContents
          section={{
            header: { title: 'Assets to Count' },
          }}
        >
          <div className="rm-padding table-wrap">
            {!selectedCategory ? (
              <EmptyList
                title="No Category Selected"
                subtitle="Select an asset category to see assets to count"
              />
            ) : assetsForCount.length === 0 ? (
              <EmptyList
                title="No Assets Found"
                subtitle="No approved assets found for this category"
              />
            ) : (
              <List className="core-list content-wrap full" disablePadding>
                {assetsForCount.map((asset, index) => {
                  const hasDiscrepancy =
                    asset.counted_qty !== null &&
                    asset.counted_qty !== asset.expected_qty
                  const isCounted =
                    asset.counted_qty !== null &&
                    asset.counted_qty !== undefined

                  return (
                    <ListItem
                      key={`${asset.fa_asset_id}-${asset.cost_centre_id}-${asset.location_id}`}
                      className="table-listItem"
                      divider
                      style={{
                        backgroundColor: hasDiscrepancy ? '#fff3e0' : 'white',
                        paddingTop: '12px',
                        paddingBottom: '12px',
                      }}
                    >
                      <ListItemText
                        primary={
                          <Grid
                            container
                            justifyContent="flex-start"
                            spacing={0}
                            alignItems="center"
                          >
                            <Grid
                              item
                              xs={8}
                              style={{ display: 'flex', alignItems: 'center' }}
                            >
                              <div
                                className="xsTitle"
                                style={{ lineBreak: 'anywhere' }}
                              >
                                {index + 1}. {asset.name}
                              </div>
                              {isCounted &&
                                (hasDiscrepancy ? (
                                  <WarningIcon
                                    style={{
                                      marginLeft: '8px',
                                      color: 'orange',
                                      fontSize: '18px',
                                    }}
                                  />
                                ) : (
                                  <CheckCircleIcon
                                    style={{
                                      marginLeft: '8px',
                                      color: 'green',
                                      fontSize: '18px',
                                    }}
                                  />
                                ))}
                            </Grid>
                            <Grid item xs={4} style={{ textAlign: 'right' }}>
                              <span className="desc" style={{ color: '#666' }}>
                                {asset.asset_card_no}
                              </span>
                            </Grid>
                          </Grid>
                        }
                        secondary={
                          <>
                            <Grid container alignItems="center" spacing={1}>
                              <Grid item xs={6}>
                                <span style={{ fontSize: '12px' }}>
                                  {asset.cost_centre_name}
                                </span>
                              </Grid>
                              <Grid
                                item
                                xs={6}
                                style={{
                                  textAlign: 'right',
                                  display: 'flex',
                                  justifyContent: 'flex-end',
                                  alignItems: 'center',
                                }}
                              >
                                {editingAssetIndex === index ? (
                                  <NumberFormat
                                    customInput={TextField}
                                    value={asset.counted_qty ?? ''}
                                    onValueChange={({ floatValue }) => {
                                      handleCountedQtyChange(
                                        index,
                                        floatValue ?? null
                                      )
                                    }}
                                    thousandSeparator
                                    decimalScale={2}
                                    allowNegative={false}
                                    style={{ width: '80px' }}
                                    size="small"
                                    autoFocus
                                    onBlur={() => setEditingAssetIndex(null)}
                                    placeholder="Count"
                                  />
                                ) : (
                                  <>
                                    <span
                                      style={{
                                        fontSize: '12px',
                                        marginRight: '4px',
                                      }}
                                    >
                                      {asset.counted_qty ?? '-'} /
                                    </span>
                                    <span
                                      style={{
                                        fontSize: '12px',
                                        color: '#F39C12',
                                      }}
                                    >
                                      {asset.expected_qty}
                                    </span>
                                    <IconButton
                                      size="small"
                                      onClick={() =>
                                        setEditingAssetIndex(index)
                                      }
                                      style={{ marginLeft: '4px' }}
                                    >
                                      <EditIcon style={{ fontSize: '16px' }} />
                                    </IconButton>
                                  </>
                                )}
                              </Grid>
                            </Grid>
                            <Grid container alignItems="center" spacing={1}>
                              <Grid
                                item
                                xs={12}
                                style={{
                                  display: 'flex',
                                  alignItems: 'center',
                                }}
                              >
                                <LocationOnIcon
                                  style={{
                                    fontSize: 16,
                                    marginRight: 4,
                                    color: '#F39C12',
                                  }}
                                />
                                <span style={{ fontSize: '12px' }}>
                                  {asset.location_name}
                                </span>
                              </Grid>
                            </Grid>
                            {/* {editingAssetIndex === index && ( */}
                            <Grid
                              container
                              alignItems="center"
                              spacing={1}
                              style={{ marginTop: '4px' }}
                            >
                              <Grid item xs={12}>
                                <TextField
                                  placeholder="Add note"
                                  value={asset.count_note || ''}
                                  onChange={e =>
                                    handleCountNoteChange(index, e.target.value)
                                  }
                                  fullWidth
                                  size="small"
                                  multiline
                                  variant="outlined"
                                  style={{ fontSize: '12px' }}
                                />
                              </Grid>
                            </Grid>
                            {/* )} */}
                          </>
                        }
                      />
                    </ListItem>
                  )
                })}
              </List>
            )}
          </div>
        </CardContents>
      </ContentWrapper>

      {footerOptions?.length > 0 && (
        <AccountFooter options={[...footerOptions]} />
      )}

      <ErrorDialog
        errorDia={errorDia}
        setErrorDia={setErrorDia}
        errorMsg={errMsg}
        errorHeaderMsg={'Error'}
      />

      <ExitConfirmationDialog
        openExitConf={openExitConf}
        setOpenExitConf={setOpenExitConf}
        onConfirm={() => {
          history.push(`/fixed-assets/${CompanyID}/assets-count`)
        }}
      />
    </>
  )
}
